<!-- src/views/pages/reports/PharmacyReportsList.vue -->
<template>
  <div>
    <!-- Loader -->
    <div
      v-if="showLoader"
      class="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50"
    >
      <svg
        class="animate-spin h-16 w-16 text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" />
      </svg>
    </div>

    <!-- Filters -->
    <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
      <div class="flex flex-wrap items-center gap-4">
        <!-- Text Filter Dropdown -->
        <div class="flex items-center space-x-3">
          <div class="relative inline-block">
            <button
              @click="toggleDropdown"
              class="inline-flex items-center px-4 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 transition"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              {{ selectedFilter ? filterOptions[selectedFilter] : 'Filter by' }}
              <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <div
              v-if="isDropdownVisible"
              class="absolute top-full left-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
            >
              <div class="py-1">
                <button @click="setFilter('visit_code')" class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Visit Code</button>
                <button @click="setFilter('patient_name')" class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Patient Name</button>
                <button @click="setFilter('drug_name')" class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Drug Name</button>
                <button @click="setFilter('prescription_status')" class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Status</button>
              </div>
            </div>
          </div>

          <!-- Search Input -->
          <input
            v-if="selectedFilter"
            v-model="filterValue"
            type="text"
            :placeholder="`Search ${filterOptions[selectedFilter].toLowerCase()}...`"
            class="w-64 px-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-custom-yellow focus:border-transparent"
          />

          <button
            v-if="selectedFilter"
            @click="clearTextFilter"
            class="px-4 py-2 bg-red-500 text-white rounded-md text-sm hover:bg-red-600"
          >
            Clear
          </button>
        </div>

        <!-- Date Range -->
        <div class="flex items-center space-x-3 text-sm">
          <div class="flex items-center space-x-2">
            <label class="font-medium text-gray-700">From:</label>
            <input v-model="dateFrom" type="date" class="px-3 py-2 border rounded-md focus:ring-2 focus:ring-custom-yellow" />
          </div>
          <div class="flex items-center space-x-2">
            <label class="font-medium text-gray-700">To:</label>
            <input v-model="dateTo" type="date" class="px-3 py-2 border rounded-md focus:ring-2 focus:ring-custom-yellow" />
          </div>
          <button
            v-if="dateFrom || dateTo"
            @click="clearDateFilter"
            class="text-red-600 hover:text-red-800 font-medium"
          >
            Clear Dates
          </button>
        </div>

        <!-- Apply Button -->
        <button
          @click="applyFilters"
          class="px-6 py-2 bg-custom-yellow text-custom-blue font-semibold rounded-md hover:bg-yellow-400 transition"
        >
          Apply Filters
        </button>
      </div>
    </div>

    <!-- Export -->
    <div class="flex justify-end gap-3 mb-6">
      <button
        @click="exportCurrentPage"
        :disabled="prescriptions.length === 0"
        class="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H8a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Export Page ({{ prescriptions.length }})
      </button>
      <button
        @click="exportAll"
        :disabled="!meta || meta.total_items === 0 || exportingAll"
        class="px-4 py-2 bg-green-700 text-white rounded-md text-sm font-medium hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H8a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        {{ exportingAll ? 'Exporting...' : `Export All (${meta?.total_items || 0})` }}
      </button>
    </div>

    <!-- Summary Cards -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" v-if="summary">
      <div class="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <p class="text-sm text-blue-600 font-medium">Total Prescriptions</p>
        <p class="text-2xl font-bold text-blue-900">{{ summary.total_prescriptions }}</p>
      </div>
      <div class="bg-green-50 p-4 rounded-lg border border-green-200">
        <p class="text-sm text-green-600 font-medium">Total Revenue</p>
        <p class="text-2xl font-bold text-green-900">KES {{ Number(summary.total_revenue).toLocaleString() }}</p>
      </div>
      <div class="bg-purple-50 p-4 rounded-lg border border-purple-200">
        <p class="text-sm text-purple-600 font-medium">Issued</p>
        <p class="text-2xl font-bold text-purple-900">{{ summary.prescriptions_by_status['Issued'] || 0 }}</p>
      </div>
      <div class="bg-orange-50 p-4 rounded-lg border border-orange-200">
        <p class="text-sm text-orange-600 font-medium">Pending</p>
        <p class="text-2xl font-bold text-orange-900">{{ summary.prescriptions_by_status['Pending'] || 0 }}</p>
      </div>
    </div>

    <!-- Table -->
    <div class="relative overflow-x-auto shadow-md sm:rounded-sm mt-5">
      <table class="w-full text-left rtl:text-right text-gray-700">
        <thead>
          <tr class="bg-gray-100">
            <th class="px-6 py-3">Visit Code</th>
            <th class="px-6 py-3">Visit Date</th>
            <th class="px-6 py-3">Patient Name</th>
            <th class="px-6 py-3">Drug Name</th>
            <th class="px-6 py-3">Quantity</th>
            <th class="px-6 py-3">Status</th>
            <th class="px-6 py-3">Cost</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item) in prescriptions"
            :key="item.prescription_id"
            class="border-b hover:bg-gray-50"
          >
            <td class="px-6 py-2 font-medium">{{ item.visit_code }}</td>
            <td class="px-6 py-2">{{ formatDate(item.visit_date) }}</td>
            <td class="px-6 py-2">{{ item.patient_full_name }}</td>
            <td class="px-6 py-2">{{ item.drug_name }}</td>
            <td class="px-6 py-2 text-center">{{ item.quantity }}</td>
            <td class="px-6 py-2">
              <span
                class="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                :class="statusClass(item.status)"
              >
                {{ item.status }}
              </span>
            </td>
            <td class="px-6 py-2 font-semibold text-left">
              KES {{ Number(item.cost).toLocaleString() }}
            </td>
          </tr>
          <tr v-if="prescriptions.length === 0">
            <td colspan="7" class="px-6 py-12 text-center text-gray-500">
              No prescriptions found matching your filters.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- REUSABLE PAGINATION COMPONENT -->
    <PaginationComponent
      v-if="meta"
      v-model:currentPage="currentPage"
      v-model:itemsPerPage="perPage"
      :totalPages="meta.total_pages"
      :totalItems="meta.total_items"
      @update:currentPage="handlePageChange"
      @update:changePageSize="handlePageSizeChange"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { useToast } from 'vue-toast-notification'
import apiClient from '@/services/api'
import { debounce } from 'lodash-es'
import PaginationComponent from '@/components/shared/pagination.vue'
import { exportRowsToExcel, todayStamp } from '@/utils/excelExport'

const toast = useToast()

// State
const showLoader = ref(true)
const prescriptions = ref([])
const summary = ref(null)
const meta = ref(null)
const exportingAll = ref(false)

const isDropdownVisible = ref(false)
const selectedFilter = ref(null)
const filterValue = ref('')
const dateFrom = ref('')
const dateTo = ref('')

const currentPage = ref(1)
const perPage = ref(10)

const filterOptions = {
  visit_code: 'Visit Code',
  patient_name: 'Patient Name',
  drug_name: 'Drug Name',
  prescription_status: 'Status'
}

const exportColumns = [
  { key: 'visit_code', label: 'Visit Code' },
  { key: 'visit_date', label: 'Visit Date', format: (v) => (v ? formatDate(v) : '') },
  { key: 'patient_full_name', label: 'Patient Name' },
  { key: 'drug_name', label: 'Drug Name' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'status', label: 'Status' },
  { key: 'cost', label: 'Cost (KES)', format: (v) => Number(v || 0) },
]

// Dropdown
const toggleDropdown = () => {
  isDropdownVisible.value = !isDropdownVisible.value
}

const setFilter = (key) => {
  selectedFilter.value = key
  isDropdownVisible.value = false
  filterValue.value = ''
}

const clearTextFilter = () => {
  selectedFilter.value = null
  filterValue.value = ''
  currentPage.value = 1
  applyFilters()
}

const clearDateFilter = () => {
  dateFrom.value = ''
  dateTo.value = ''
  currentPage.value = 1
  applyFilters()
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const statusClass = (status) => {
  const s = status?.toLowerCase() || ''
  if (s.includes('issued')) return 'bg-green-100 text-green-800'
  if (s.includes('pending')) return 'bg-yellow-100 text-yellow-800'
  if (s.includes('progress') || s.includes('available')) return 'bg-blue-100 text-blue-800'
  if (s.includes('reject') || s.includes('out of stock')) return 'bg-red-100 text-red-800'
  return 'bg-gray-100 text-gray-800'
}

// Pagination
const handlePageChange = (page) => {
  currentPage.value = page
  applyFilters()
}

const handlePageSizeChange = (size) => {
  perPage.value = Number(size)
  currentPage.value = 1
  applyFilters()
}

const buildCurrentFilter = () => {
  const filter = {}
  if (selectedFilter.value && filterValue.value.trim()) {
    filter[selectedFilter.value] = filterValue.value.trim()
  }
  if (dateFrom.value) filter.from_date = dateFrom.value
  if (dateTo.value) filter.to_date = dateTo.value
  return filter
}

const applyFilters = async () => {
  showLoader.value = true
  try {
    const payload = {
      page: currentPage.value,
      per_page: perPage.value,
    }

    const filter = buildCurrentFilter()
    if (Object.keys(filter).length > 0) {
      payload.filter = filter
    }

    const response = await apiClient.getPharmacyReports(payload)

    if (response.success) {
      prescriptions.value = response.data
      meta.value = response.meta

      // Fetch stats
      const statsPayload = {}
      if (dateFrom.value) statsPayload.from_date = dateFrom.value
      if (dateTo.value) statsPayload.to_date = dateTo.value
      if (Object.keys(filter).length > 0) statsPayload.filter = filter

      const statsRes = await apiClient.getPharmacyStats(statsPayload)
      if (statsRes.success) summary.value = statsRes.data

      toast.success('Pharmacy reports loaded successfully.')
    } else {
      toast.error(response.message || 'Failed to load data')
    }
  } catch (err) {
    console.error(err)
    toast.error('Network error')
  } finally {
    showLoader.value = false
  }
}

const exportCurrentPage = () => {
  if (prescriptions.value.length === 0) {
    toast.error('No records on this page to export.')
    return
  }
  exportRowsToExcel(prescriptions.value, exportColumns, `pharmacy-reports-page-${currentPage.value}-${todayStamp()}.xlsx`)
}

const exportAll = async () => {
  if (!meta.value || meta.value.total_items === 0) {
    toast.error('No records to export.')
    return
  }
  exportingAll.value = true
  try {
    const filter = buildCurrentFilter()
    const payload = { page: 1, per_page: meta.value.total_items }
    if (Object.keys(filter).length > 0) payload.filter = filter

    const response = await apiClient.getPharmacyReports(payload)
    if (response.success) {
      exportRowsToExcel(response.data, exportColumns, `pharmacy-reports-all-${todayStamp()}.xlsx`)
      toast.success(`Exported ${response.data.length} record(s).`)
    } else {
      toast.error(response.message || 'Failed to export all records.')
    }
  } catch (err) {
    console.error(err)
    toast.error('Failed to export all records.')
  } finally {
    exportingAll.value = false
  }
}

// Debounced search
watch(filterValue, debounce(() => {
  if (!filterValue.value || filterValue.value.length >= 3) {
    currentPage.value = 1
    applyFilters()
  }
}, 600))

watch([dateFrom, dateTo], () => {
  currentPage.value = 1
  debounce(applyFilters, 800)()
})

onMounted(() => {
  applyFilters()
})
</script>

<style scoped>
.focus\:ring-custom-yellow:focus {
  --tw-ring-color: #facc15;
}
</style>
